import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useOnboardingContext } from '@/contexts/onboardingContext';
import useTranslate from '@/hooks/useTranslate';
import { useJurisdictions } from '@/pages/members/hooks/useJurisdictions';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import { StepProps } from '@/pages/members/pages/Onboarding/steps/types';
import getSubmittedData from '@/pages/members/pages/Onboarding/utils/getSubmittedData';
import Alert from '@/ui/members/components/Alert';
import FieldError from '@/ui/members/components/form/FieldError';
import InputGroup from '@/ui/members/components/form/InputGroup';
import SelectDialog from '@/ui/members/components/form/SelectDialog';
import Spinner from '@/ui/members/components/Spinner';
import Heading from '@/ui/members/components/typography/Heading/Heading';

interface WhereDoYouLiveFormValues extends Record<string, string | undefined> {
  jurisdiction: string;
  jurisdictionStatus?: string;
}

interface Props extends StepProps {
  /**
   * Whether to show all content or a minimal version
   */
  showAllContent?: boolean;
}

const WhereDoYouLive = ({ nodeType, showAllContent = true, uid }: Props) => {
  const onboardingContext = useOnboardingContext();
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.WhereDoYouLive`);

  const {
    data: jurisdictionsResponse,
    isPending: isPendingJurisdictions,
    isError
  } = useJurisdictions();

  const formattedJurisdictions = useMemo(() => {
    return (
      jurisdictionsResponse?.map((jurisdiction) => ({
        label: jurisdiction.name,
        value: jurisdiction.name
      })) || []
    );
  }, [jurisdictionsResponse]);

  const {
    control,
    handleSubmit: defaultHandleSubmit,
    formState: { errors, isValid }
  } = useForm<WhereDoYouLiveFormValues>({
    mode: 'onSubmit',
    defaultValues: (getSubmittedData(
      nodeType,
      uid,
      onboardingContext.steps
    ) as WhereDoYouLiveFormValues) || {
      jurisdiction: ''
    }
  });

  const handleSubmit = (callback: (data: WhereDoYouLiveFormValues) => void) =>
    defaultHandleSubmit((data) => {
      callback({
        ...data,
        jurisdictionStatus:
          jurisdictionsResponse?.find((j) => j.name === data.jurisdiction)?.status || ''
      });
    });

  return (
    <StepComponent handleSubmit={handleSubmit} uid={uid} nodeType={nodeType} isValid={isValid}>
      {isError && (
        <Alert level="error">
          <p>{t('errorFetchingStates')}</p>
        </Alert>
      )}

      <Heading className="text-center" value={1}>
        {t('heading')}
      </Heading>

      {showAllContent && <p className="text-center">{t('subheading')}</p>}

      <InputGroup>
        <Controller
          defaultValue=""
          name="jurisdiction"
          control={control}
          rules={{
            required: t('stateRequired')
          }}
          render={({ field }) => (
            <>
              {isPendingJurisdictions ? (
                <Spinner />
              ) : (
                <SelectDialog
                  autoFocus
                  id="jurisdiction"
                  label={t('stateLabel')}
                  onChange={field.onChange}
                  options={formattedJurisdictions}
                  selected={field.value}
                />
              )}
            </>
          )}
        />

        <FieldError error={errors.jurisdiction} />
      </InputGroup>
    </StepComponent>
  );
};

export default WhereDoYouLive;
